// React & Hooks
import { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Material UI Components
import { Box, Stack, Button, Fade } from "@mui/material";

// Contexts
import { AuthContext } from "../../Context/AuthContext";

// Custom Hooks
import useGetClassById from "../../Hooks/useGetClassById";

// UI Components
import { PageHeaderTitle } from "../../Utils/Component";

// Page Styles
import "../../Style/pagestyle.scss";

// Feature Components 
import Student from "./Student/Student";  
import Schedule from "./Schedule/Schedule";
import Homework from "./Homework/Homework";
import ExamScore from "./Exam/ExamScore";
import Attendance from "./Attendance/Attendance";
import TeachingTime from "./TeachingTime/TeachingTime";

export default function StudyClassDetailForTeacher() {
  // Translation context
  const { t } = useContext(AuthContext);

  // Read tab from localStorage or set default
  const handleGetItemTabFromLocalStorage = () => 
    window.localStorage.getItem("student_tab");

  const [tab, setTab] = useState(
    handleGetItemTabFromLocalStorage() || "Student"
  );

  // Get classId from query parameters
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const classId = params.get("classId"); 
  let teacherId = params.get("teacherId");
  // Fetch class data
  const { classData } = useGetClassById({ id: classId });

  // Persist tab selection to localStorage
  useEffect(() => {
    window.localStorage.setItem("student_tab", tab);
  }, [tab]);

  // Mapping tabs to components
  const TabComponents = {
    Student,
    Schedule,
    Homework,
    ExamScore,
    Attendance,
    TeachingTime,
  };

  const ActiveComponent = TabComponents[tab];

  // Props passed to each tab component
  const tabPropsMap = {
    Student: { classId, defaultTotalHour: classData?.totalHour },
    Homework: { classId },
    Schedule: { classId, classData },
    ExamScore: { classId },
    Attendance: { classId },
    TeachingTime: { classId },
  };

  const activeProps = tabPropsMap[tab] || {};

  // Tab button definitions
  const tabs = [
    { key: "Student", label: t("page_student") },
    { key: "Schedule", label: t("thead_schedule") },
    { key: "Attendance", label: t("thead_attendance") },
    { key: "Homework", label: t("thead_homework") },
    { key: "TeachingTime", label: t("thead_teaching_time") },
    { key: "ExamScore", label: t("thead_exam") },
  ];

  return (
    <div className="page-container">
      <PageHeaderTitle
        classPage={t("class-page-title")}
        classSlash="slash-title" 
        routeTo={`/teacher/teacher-detail?teacherId=${teacherId}`} 
        pageTitle={t("page_class")}
        detailTitle={ 
          classData?.courseId?.enName
            ? classData?.courseId?.enName + " " + classData?.className
            : ""
        }
      />

      {/* Tab Navigation */}
      <Box
        className="tab-container"
        sx={{ position: "relative", marginTop: 8 }}
      >
        <Box sx={{ position: "absolute", bottom: "-2px", width: "100%" }}>
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" className="tab">
              {tabs.map(({ key, label }) => (
                <Button
                  key={key}
                  className={
                    tab === key
                      ? t("class-name-tab-item-active")
                      : t("class-name-tab-item")
                  }
                  onClick={() => setTab(key)}
                >
                  {label}
                </Button>
              ))}
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Tab Content with Fade Transition */}
      <Box>
        <Fade in={true} key={tab} timeout={400}>
          <div>{ActiveComponent && <ActiveComponent {...activeProps} />}</div>
        </Fade>
      </Box>
    </div>
  );
}
